import { useCallback } from 'react';
import { useTranslation } from '@frontend/i18n';
import { CareCreditMessageConsent } from '@frontend/payments-care-credit';
import { useFormField } from '@frontend/design-system';
import { useValidPhoneNumberFromInvoice } from '../../../hooks/use-valid-phone-number-from-invoice';
import { useValidPhoneNumberFromPerson } from '../../../hooks/use-valid-phone-number-from-person';
import { useCollectPaymentMultiContext } from '../../collect-payment-multi.context';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../utils/steps';
import { StepPerformanceWrapper } from '../atoms/step-performance-wrapper';
import { StyledStep } from '../atoms/styled-step';

export const CareCreditMessageDisclosure = () => {
  return (
    <StepPerformanceWrapper step={PAYMENT_MULTI_STEPS.careCreditMessageDisclosure}>
      <CareCreditMessageDisclosureBody />
    </StepPerformanceWrapper>
  );
};

export const CareCreditMessageDisclosureBody = () => {
  const { t } = useTranslation('payments');

  const {
    invoice,
    person,
    careCreditMessageType,
    careCreditData,
    activeLocationId,
    multiStep: { onCancel, goNextStep, goPrevStep },
  } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber: firstInvoicePhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  /**
   * Thanks to the CareCredit payments panel, we might have a flow with no invoice.
   * So we needto have a person fallback, too. Internally, the CareCredit multi-steppers only do the valid SMS lookup
   * via person's id, rather than invoice.person.id, etc.
   */
  const { firstValidPhoneNumber: firstPersonPhoneNumber } = useValidPhoneNumberFromPerson(activeLocationId, person);

  const checkboxProps = useFormField({
    type: 'checkbox',
    required: true,
    value: false,
  });

  const phoneNumber = firstInvoicePhoneNumber || firstPersonPhoneNumber;

  const isPaymentFlow = careCreditMessageType === 'payment-link';

  const onClickPrimary = useCallback(() => {
    goNextStep({
      orStep: PAYMENT_MULTI_STEPS.careCreditMessage,
    });
  }, [goNextStep]);

  const onClickSecondary = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const onClickBack = useCallback(() => {
    if (careCreditData?.status === 'CARDHOLDER') {
      goPrevStep({
        orStep: PAYMENT_MULTI_STEPS.careCreditCollect,
      });
    } else if (careCreditData?.status === 'PREQUALIFICATION_NEEDED') {
      goPrevStep({
        orStep: PAYMENT_MULTI_STEPS.careCreditEnrollDisclosure,
      });
    } else if (careCreditMessageType === 'pre-approved-link') {
      goPrevStep({
        orStep: PAYMENT_MULTI_STEPS.careCreditPreapprovedDisclosure,
      });
    } else if (careCreditMessageType === 'payment-link') {
      goPrevStep({
        orStep: PAYMENT_MULTI_STEPS.careCreditCollect,
      });
    } else {
      goPrevStep({
        orStep: PAYMENT_MULTI_STEPS.careCreditEnrollDisclosure,
      });
    }
  }, [careCreditData?.status, careCreditMessageType, goPrevStep]);

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.careCreditMessageDisclosure}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditMessageDisclosure]}
      hasInternalFooter
      footer={{
        primary: {
          disabled: !checkboxProps.value || !phoneNumber,
          children: t('Next'),
        },
        secondary: {
          children: t('Cancel'),
        },
        onClickPrimary,
        onClickSecondary,
        onClickBack,

        /**
         * This step might be reached from the payments panel, where there is no invoice.
         * In which case, hide the back button, as there's no "payment methods" step to go back to.
         */
        showBackButton: !!invoice,
      }}
    >
      <CareCreditMessageConsent checkboxProps={checkboxProps} isPayment={isPaymentFlow} phoneNumber={phoneNumber} />
    </StyledStep>
  );
};
